import { FC, SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Tab, Tabs } from '@mui/material'

import { CompareTab } from '../../hooks/useUAInsightsNavigation'

interface CompareResultTabsProps {
  activeTab: CompareTab
  onTabChange: (event: SyntheticEvent, nextTab: CompareTab) => void
  /** When true, render the smaller "compact" strip used once the sticky header collapses. */
  compact: boolean
  disabled: boolean
}

/**
 * In-page tab navigation for the UA Insights Compare page (Overview /
 * Motivations / Story Patterns / Creative Tags). Mirrors the game page's
 * `GamePageTabs` styling so the Compare page reads as a continuation of the
 * game page it was opened from. The active tab is persisted to the URL as the
 * `compareTab` query param by the parent page.
 */
export const CompareResultTabs: FC<CompareResultTabsProps> = ({ activeTab, onTabChange, compact, disabled }) => {
  const { t } = useTranslation('ua-insights')

  const minHeight = compact ? 32 : 38

  return (
    <Tabs
      value={activeTab}
      onChange={onTabChange}
      textColor="primary"
      indicatorColor="primary"
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={{
        minHeight,
        // Nudge the row down 1px so the active-tab indicator sits flush on top
        // of the sticky header's bottom border instead of floating a hairline
        // above it.
        transform: 'translateY(1px)',
        '& .MuiTab-root': { minHeight, py: compact ? 0 : 0.5, px: compact ? 1.5 : 2, textTransform: 'none', fontWeight: 'bold' },
        '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0, width: 0 },
      }}
    >
      <Tab value="overview" label={t('compare_tab_overview')} disabled={disabled} />
      <Tab value="motivations" label={t('compare_tab_motivations')} disabled={disabled} />
      <Tab value="storyPatterns" label={t('compare_tab_story_patterns')} disabled={disabled} />
      <Tab value="creativeTags" label={t('compare_tab_creative_tags')} disabled={disabled} />
    </Tabs>
  )
}
